import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";

interface ComponentsMap {
  skills: string[];
  scripts: string[];
  commands: string[];
  mcp_servers: string[];
}

interface Props {
  open: boolean;
  components: ComponentsMap;
  current?: Record<string, string[]> | null;
  confirmLabel?: string;
  onConfirm: (mask: Record<string, string[]>) => void;
  onClose: () => void;
}

type Kind = keyof ComponentsMap;
const KINDS: Kind[] = ["skills", "scripts", "commands", "mcp_servers"];

function buildInitialState(
  components: ComponentsMap,
  current: Record<string, string[]> | null | undefined,
): Record<string, Set<string>> {
  // The backend serializes an unconfigured `selected` as JSON null, which
  // arrives here as `current === null`. Treat null and undefined alike:
  // both mean "no explicit selection → default-all".
  current = current ?? undefined;
  const state: Record<string, Set<string>> = {};
  for (const kind of KINDS) {
    const names = components[kind];
    if (!names || names.length === 0) continue;
    if (current === undefined) {
      // all checked by default
      state[kind] = new Set(names);
    } else if (current[kind] === undefined) {
      // kind absent in current = all selected
      state[kind] = new Set(names);
    } else {
      // only the explicitly listed names are checked
      state[kind] = new Set(current[kind].filter((n) => names.includes(n)));
    }
  }
  return state;
}

export function ComponentSelectionDialog({
  open,
  components,
  current,
  confirmLabel = "Confirm",
  onConfirm,
  onClose,
}: Props) {
  const [checked, setChecked] = useState<Record<string, Set<string>>>(() =>
    buildInitialState(components, current),
  );

  useEffect(() => {
    if (open) setChecked(buildInitialState(components, current));
  }, [open, components, current]);

  function toggle(kind: string, name: string) {
    setChecked((prev) => {
      const next = { ...prev };
      const set = new Set(prev[kind] ?? []);
      if (set.has(name)) {
        set.delete(name);
      } else {
        set.add(name);
      }
      next[kind] = set;
      return next;
    });
  }

  function handleConfirm() {
    const mask: Record<string, string[]> = {};
    for (const kind of KINDS) {
      const names = components[kind];
      if (!names || names.length === 0) continue;
      const checkedNames = checked[kind] ?? new Set();
      // Only include in mask if NOT all are checked
      if (checkedNames.size < names.length) {
        // preserve original order
        mask[kind] = names.filter((n) => checkedNames.has(n));
      }
    }
    onConfirm(mask);
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Select Components</DialogTitle>
      <DialogContent>
        {KINDS.map((kind) => {
          const names = components[kind];
          if (!names || names.length === 0) return null;
          return (
            <div key={kind}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, textTransform: "capitalize" }}>
                {kind.replaceAll("_", " ")}
              </Typography>
              {names.map((name) => (
                <FormControlLabel
                  key={name}
                  label={name}
                  control={
                    <Checkbox
                      checked={checked[kind]?.has(name) ?? false}
                      onChange={() => toggle(kind, name)}
                      slotProps={{ input: { "aria-label": name } as React.InputHTMLAttributes<HTMLInputElement> }}
                    />
                  }
                />
              ))}
            </div>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
